import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import Lightbox from "react-image-lightbox";
import Cookies from "js-cookie";
import "react-image-lightbox/style.css";
import { AiFillStar } from "react-icons/ai";
import { Context as AuthContext } from "../../../context/AuthContext";
import LazyImage from "../../common/LazyImage";
import {
  createHotelBooking,
  verifyHotelPayment,
  loadRazorpay,
  toYMD,
} from "./bookingapi";
import {
  HOTEL_BOOKING_STEPS,
  HOTEL_CONTENT_NAV,
  hotelAmenityIcon,
} from "./hotelData";

const StarRating = ({ rating = 0 }) => {
  const safeRating = Math.max(0, Math.min(5, Number(rating) || 0));

  return (
    <div className="flex" aria-label={`${safeRating} out of 5 stars`}>
      {[...Array(5)].map((_, i) => (
        <AiFillStar
          key={i}
          color={i < Math.round(safeRating) ? "#FFD700" : "#ccc"}
          size={18}
        />
      ))}
    </div>
  );
};

const dayMs = 24 * 60 * 60 * 1000;

const nightsBetween = (checkIn, checkOut) => {
  if (!checkIn || !checkOut) return 0;
  const a = new Date(checkIn);
  const b = new Date(checkOut);
  a.setHours(0, 0, 0, 0);
  b.setHours(0, 0, 0, 0);
  return Math.max(0, Math.round((b - a) / dayMs));
};
const GuestCounter = ({ label, hint, value, onChange, min, max }) => (
  <div className="ht-counter">
    <div className="ht-counter__text">
      <span className="ht-counter__label">{label}</span>
      <span className="ht-counter__hint">{hint}</span>
    </div>
    <div className="ht-counter__controls">
      <button
        type="button"
        className="ht-counter__btn"
        disabled={value <= min}
        onClick={() => onChange(value - 1)}
        aria-label={`Decrease ${label}`}
      >
        <i className="fa-solid fa-minus" aria-hidden="true" />
      </button>
      <strong className="ht-counter__value" aria-live="polite">
        {value}
      </strong>
      <button
        type="button"
        className="ht-counter__btn"
        disabled={value >= max}
        onClick={() => onChange(value + 1)}
        aria-label={`Increase ${label}`}
      >
        <i className="fa-solid fa-plus" aria-hidden="true" />
      </button>
    </div>
  </div>
);

const HotelDetailView = ({
  hotel,
  nearbyHotels,
  navigate,
  activeTab,
  onTabClick,
}) => {
  const { state, openLoginModal } = useContext(AuthContext);
  const [bannerSlide, setBannerSlide] = useState(0);
  const [aboutExpanded, setAboutExpanded] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [bookingStep, setBookingStep] = useState(1);
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [roomDetails, setRoomDetails] = useState(null);
  const [roomLoading, setRoomLoading] = useState(false);
  const [roomError, setRoomError] = useState("");
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [guestName, setGuestName] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [specialRequest, setSpecialRequest] = useState("");
  const [paying, setPaying] = useState(false);
  const [bookingError, setBookingError] = useState("");
  const [confirmedBooking, setConfirmedBooking] = useState(null);

  const guests = adults + children; // existing code that reads `guests` keeps working
  const resetGuests = () => {
    setAdults(1);
    setChildren(0);
  };

  // Prefill contact details from the logged-in user
  useEffect(() => {
    const u = state.userData;
    if (!u) return;
    setGuestName((v) => v || u.name || "");
    setGuestPhone((v) => v || u.phone || u.mobile || "");
    setGuestEmail((v) => v || u.email || "");
  }, [state.userData]);

  const images = hotel.images?.length
    ? hotel.images
    : [hotel.image].filter(Boolean);
  const aboutDescription =
    hotel.description?.trim() || "No description available.";
  const aboutNeedsToggle = aboutDescription.length > 260;
  const displayImages = roomDetails
    ? [roomDetails.featured_image, ...(roomDetails.images || [])].filter(
        Boolean,
      )
    : images;

  const heroImg = displayImages[bannerSlide] || displayImages[0];
  const navItems = HOTEL_CONTENT_NAV;

  const selectedRoom = useMemo(
    () =>
      hotel.rooms?.find((r) => String(r.id) === String(selectedRoomId)) || null,
    [hotel.rooms, selectedRoomId],
  );

  const nights = nightsBetween(checkIn, checkOut);
  const totalPrice =
    nights > 0 && selectedRoom ? nights * selectedRoom.price : 0;

  const phoneOk = /^[6-9]\d{9}$/.test(guestPhone);
  const emailOk = /^\S+@\S+\.\S+$/.test(guestEmail);

  const canBook =
    checkIn &&
    checkOut &&
    selectedRoomId &&
    nights > 0 &&
    adults > 0 &&
    guestName.trim() &&
    phoneOk &&
    emailOk;

  const maxAdults = roomDetails?.max_adults || selectedRoom?.capacity || 4;
  const maxChildren = roomDetails?.max_children ?? 2;

  const hasBookingDraft = checkIn || checkOut || selectedRoomId || guests > 0;

  useEffect(() => {
    if (bannerSlide >= images.length) setBannerSlide(0);
  }, [images.length, bannerSlide]);

  useEffect(() => {
    setAboutExpanded(false);
    setBannerSlide(0);
    setCheckIn(null);
    setCheckOut(null);
    setSelectedRoomId(null);
    resetGuests();
    setBookingStep(1);
  }, [hotel?.id]);

  useEffect(() => {
    if (!checkIn) {
      setBookingStep(1);
      return;
    }
    if (!checkOut) {
      setBookingStep(2);
      return;
    }
    if (!selectedRoomId) {
      setBookingStep(3);
      return;
    }
    if (!guests) {
      setBookingStep(4);
      return;
    }
    setBookingStep(4);
  }, [checkIn, checkOut, selectedRoomId, guests]);

  const scrollFromTabRef = useRef(false);
  const sideNavRef = useRef(null);
  const activeTabRef = useRef(activeTab);

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  const selectTab = (id, e) => {
    e?.preventDefault?.();
    scrollFromTabRef.current = true;
    onTabClick(id);
    const el = document.getElementById(`fv-section-${id}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      window.setTimeout(() => {
        scrollFromTabRef.current = false;
      }, 700);
    } else {
      scrollFromTabRef.current = false;
    }
  };

  useEffect(() => {
    const sectionEls = navItems
      .map((item) => document.getElementById(`fv-section-${item.id}`))
      .filter(Boolean);

    if (!sectionEls.length) return undefined;

    let rafId = null;
    const updateActiveFromScroll = () => {
      if (scrollFromTabRef.current) return;
      const navEl = sideNavRef.current;
      if (!navEl) return;

      const anchorY = navEl.getBoundingClientRect().bottom + 12;
      let bestId = null;
      let bestTop = -Infinity;

      for (const el of sectionEls) {
        const top = el.getBoundingClientRect().top;
        if (top <= anchorY && top > bestTop) {
          bestTop = top;
          bestId = el.getAttribute("data-section-id");
        }
      }

      if (bestId && bestId !== activeTabRef.current) {
        onTabClick(bestId);
      }
    };

    const onScroll = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateActiveFromScroll);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    updateActiveFromScroll();

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [navItems, onTabClick]);

  const bookingStepHints = {
    1: "Pick your check-in date",
    2: "Pick your check-out date",
    3: "Choose a room type",
    4: "How many guests?",
  };

  const formatChipDate = (d) =>
    d ? d.toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "—";

  const bookCtaLabel = () => {
    if (canBook) {
      return state.userData ? `Reserve · ₹${totalPrice}` : "Sign in to reserve";
    }
    if (bookingStep === 1) return "Choose check-in";
    if (bookingStep === 2) return "Choose check-out";
    if (bookingStep === 3) return "Choose a room";
    return "Select guests";
  };

  const scrollToBook = (e) => {
    e?.preventDefault?.();
    document
      .getElementById("fv-book-panel")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const fetchRoomDetails = async (room) => {
    if (!room?.slug || !hotel?.slug) return;

    try {
      setRoomLoading(true);
      setRoomError("");

      const response = await fetch(
        `https://admin.bookvenue.app/api/hotels/${encodeURIComponent(
          hotel.slug,
        )}/rooms/${encodeURIComponent(room.slug)}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error(`Unable to load room (${response.status})`);
      }

      const result = await response.json();
      const data = result?.data;

      if (!data) {
        throw new Error("Room details not found.");
      }

      console.log("SELECTED ROOM DETAILS:", data);

      setRoomDetails(data);
    } catch (error) {
      console.error("Room details API error:", error);
      setRoomDetails(null);
      setRoomError(error?.message || "Unable to load room details.");
    } finally {
      setRoomLoading(false);
    }
  };

  const handleReserve = async () => {
    if (!state.userData) {
      openLoginModal(!state.loginState);
      return;
    }
    if (!canBook || paying) return;

    setPaying(true);
    setBookingError("");

    try {
      const ok = await loadRazorpay();
      if (!ok)
        throw new Error(
          "Couldn't load the payment gateway. Check your internet connection.",
        );

      const token = Cookies.get("USER_TOKEN");
      const roomTypeId = roomDetails?.id ?? selectedRoom?.id;
      if (!roomTypeId)
        throw new Error("Room details are still loading. Please try again.");

      const created = await createHotelBooking(
        {
          hotel_id: hotel.id,
          room_id: selectedRoom.id,
          room_type_id: roomTypeId,
          check_in: toYMD(checkIn),
          check_out: toYMD(checkOut),
          rooms_count: 1,
          adults,
          children,
          guest_name: guestName.trim(),
          guest_phone: guestPhone,
          guest_email: guestEmail.trim(),
          special_request: specialRequest.trim(),
        },
        token,
      );

      console.log("BOOKING CREATED:", created);

      const order = created.order;
      const booking = created.data;
      if (!order?.id)
        throw new Error("Payment order was not created. Please try again.");

      // Hotel Razorpay account key: from the API if the backend sends it, otherwise from .env
      const rzpKey = created.key || process.env.REACT_APP_HOTEL_RAZORPAY_KEY_ID;
      console.log("RZP KEY:", rzpKey, "ORDER:", order.id);
      if (!rzpKey)
        throw new Error(
          "Hotel Razorpay key missing. Add REACT_APP_HOTEL_RAZORPAY_KEY_ID to .env",
        );

      const rzp = new window.Razorpay({
        key: rzpKey,
        order_id: order.id,
        amount: order.amount,
        currency: order.currency || "INR",
        name: "Book Venue",
        description: `${hotel.name} · ${selectedRoom.name}`,
        prefill: { name: guestName, email: guestEmail, contact: guestPhone },
        notes: { booking_id: String(booking?.booking_id ?? "") },
        handler: async (resp) => {
          try {
            const verified = await verifyHotelPayment(
              {
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              },
              token,
            );
            console.log("VERIFY RESPONSE:", verified);
            setConfirmedBooking({ ...booking, ...(verified.data || {}) });
            onClearBooking();
          } catch (err) {
            console.error("Verify error:", err);
            setBookingError(
              err.message ||
                "Payment received but confirmation failed. Please contact support. Don't pay again.",
            );
          } finally {
            setPaying(false);
          }
        },
        modal: {
          ondismiss: () => {
            setPaying(false);
            setBookingError("Payment cancelled. Your room is not booked yet.");
          },
        },
      });

      rzp.on("payment.failed", (r) => {
        setBookingError(
          r.error?.description || "Payment failed. Please try again.",
        );
        setPaying(false);
      });

      rzp.open();
    } catch (err) {
      console.error("Booking error:", err);
      setBookingError(err.message || "Could not start booking.");
      setPaying(false);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({
          title: hotel.name,
          text: `Check out ${hotel.name} on Book Venue`,
          url,
        });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
      }
    } catch {
      /* ignore cancel */
    }
  };

  const onClearBooking = () => {
    setCheckIn(null);
    setCheckOut(null);
    setSelectedRoomId(null);
    resetGuests();
    setBookingStep(1);
  };

  const handleCheckIn = (date) => {
    setCheckIn(date);
    setCheckOut(null);
    setSelectedRoomId(null);
    resetGuests();
  };

  const handleCheckOut = (date) => {
    setCheckOut(date);
    setSelectedRoomId(null);
    resetGuests();
  };

  const checkOutMin = checkIn
    ? new Date(new Date(checkIn).getTime() + dayMs)
    : new Date();

  const maxGuests = selectedRoom?.capacity || 4;

  const renderWizardStep = () => {
    switch (bookingStep) {
      case 1:
        return (
          <div className="fv-wizard__panel fv-wizard__panel--date">
            <Calendar
              onChange={handleCheckIn}
              value={checkIn}
              minDate={new Date()}
              showNeighboringMonth={false}
              className="fv-calendar"
            />
          </div>
        );
      case 2:
        if (!checkIn) {
          return <p className="fv-wizard__empty">Choose check-in first.</p>;
        }
        return (
          <div className="fv-wizard__panel fv-wizard__panel--date">
            <Calendar
              onChange={handleCheckOut}
              value={checkOut}
              minDate={checkOutMin}
              showNeighboringMonth={false}
              className="fv-calendar"
            />
          </div>
        );
      case 3:
        if (!checkOut) {
          return <p className="fv-wizard__empty">Choose check-out first.</p>;
        }
        return (
          <div className="fv-wizard__panel fv-wizard__panel--court">
            <div className="fv-court-rail" role="group" aria-label="Rooms">
              {(hotel.rooms || []).map((room) => {
                const selected = String(selectedRoomId) === String(room.id);
                return (
                  <button
                    key={room.id}
                    type="button"
                    aria-pressed={selected}
                    className={`fv-court-chip ${selected ? "is-selected" : ""}`}
                    onClick={() => {
                      setSelectedRoomId(room.id);
                      resetGuests();
                      setRoomDetails(null);
                      setRoomError("");
                      fetchRoomDetails(room);
                    }}
                  >
                    <span className="fv-court-chip__name">{room.name}</span>
                    <span className="fv-court-chip__price">
                      ₹{room.price}
                      <span className="fv-court-chip__unit">/ night</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      case 4:
      default:
        if (!selectedRoomId) {
          return <p className="fv-wizard__empty">Select a room first.</p>;
        }
        return (
          <div className="fv-wizard__panel ht-guest-step">
            {/* GUESTS */}
            <div className="ht-card">
              <h4 className="ht-card__title">
                <i className="fa-solid fa-users" aria-hidden="true" /> Guests
              </h4>
              <GuestCounter
                label="Adults"
                hint="Age 13+"
                value={adults}
                onChange={setAdults}
                min={1}
                max={maxAdults}
              />
              <GuestCounter
                label="Children"
                hint="Age 2–12"
                value={children}
                onChange={setChildren}
                min={0}
                max={maxChildren}
              />
            </div>

            {/* CONTACT DETAILS */}
            <div className="ht-card">
              <h4 className="ht-card__title">
                <i className="fa-solid fa-address-card" aria-hidden="true" />{" "}
                Contact details
              </h4>

              <label className="ht-field">
                <span className="ht-field__label">Full name</span>
                <span className="ht-field__control">
                  <i className="fa-solid fa-user" aria-hidden="true" />
                  <input
                    type="text"
                    placeholder="As on your ID"
                    autoComplete="name"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                  />
                </span>
              </label>

              <label className="ht-field">
                <span className="ht-field__label">Phone number</span>
                <span
                  className={`ht-field__control ${guestPhone && !phoneOk ? "is-invalid" : ""}`}
                >
                  <span className="ht-field__prefix">+91</span>
                  <input
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="10-digit mobile"
                    autoComplete="tel-national"
                    value={guestPhone}
                    onChange={(e) =>
                      setGuestPhone(e.target.value.replace(/\D/g, ""))
                    }
                  />
                </span>
                {guestPhone && !phoneOk && (
                  <span className="ht-field__error">
                    Enter a valid 10-digit mobile number
                  </span>
                )}
              </label>

              <label className="ht-field">
                <span className="ht-field__label">Email</span>
                <span
                  className={`ht-field__control ${guestEmail && !emailOk ? "is-invalid" : ""}`}
                >
                  <i className="fa-solid fa-envelope" aria-hidden="true" />
                  <input
                    type="email"
                    placeholder="Booking confirmation goes here"
                    autoComplete="email"
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                  />
                </span>
                {guestEmail && !emailOk && (
                  <span className="ht-field__error">
                    Enter a valid email address
                  </span>
                )}
              </label>

              <label className="ht-field">
                <span className="ht-field__label">
                  Special request <em>(optional)</em>
                </span>
                <span className="ht-field__control ht-field__control--area">
                  <textarea
                    rows={2}
                    placeholder="e.g. Late check-in, extra pillow"
                    maxLength={250}
                    value={specialRequest}
                    onChange={(e) => setSpecialRequest(e.target.value)}
                  />
                </span>
              </label>
            </div>

            {nights > 0 && (
              <p className="ht-nights-hint">
                <i className="fa-solid fa-moon" aria-hidden="true" /> {nights}{" "}
                night
                {nights > 1 ? "s" : ""} · check-in {hotel.checkInTime} ·
                check-out {hotel.checkOutTime}
              </p>
            )}
          </div>
        );
    }
  };

  const renderSectionContent = (sectionId) => {
    switch (sectionId) {
      case "amenities": {
        const amenities = roomDetails?.amenities || hotel.amenities || [];

        return amenities.length > 0 ? (
          <ul className="fv-amenity-grid">
            {amenities.map((amenity, index) => {
              const amenityName = amenity.name || "Amenity";

              return (
                <li key={amenityName + index}>
                  <div className="fv-amenity-card">
                    <span className="fv-amenity-card__icon" aria-hidden="true">
                      <i className={hotelAmenityIcon(amenityName)} />
                    </span>

                    <span className="fv-amenity-card__name">{amenityName}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="fv-empty">No amenities listed.</p>
        );
      }

      case "gallery": {
        const activeImages = selectedRoomId
          ? roomDetails
            ? [
                roomDetails.featured_image,
                ...(Array.isArray(roomDetails.images)
                  ? roomDetails.images
                  : []),
              ].filter(Boolean)
            : []
          : images;

        if (selectedRoomId && roomLoading) {
          return <p className="fv-empty">Loading room photos...</p>;
        }

        if (selectedRoomId && roomError) {
          return <p className="fv-empty">{roomError}</p>;
        }

        return activeImages.length > 0 ? (
          <div className="fv-gallery">
            <div className="fv-gallery__grid">
              {activeImages.map((img, idx) => (
                <button
                  key={img + idx}
                  type="button"
                  className={`fv-gallery__cell${
                    idx === 0 && activeImages.length > 2
                      ? " fv-gallery__cell--feature"
                      : ""
                  }`}
                  onClick={() => {
                    setPhotoIndex(idx);
                    setIsOpen(true);
                  }}
                >
                  <LazyImage
                    src={img}
                    alt={
                      selectedRoomId
                        ? roomDetails?.name || "Room"
                        : hotel.name || "Hotel"
                    }
                  />

                  <span className="fv-gallery__overlay" aria-hidden="true">
                    <i className="fa-solid fa-expand" />
                  </span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <p className="fv-empty">
            {selectedRoomId
              ? "No photos available for this room."
              : "No photos yet."}
          </p>
        );
      }
      case "reviews": {
        const reviews = hotel.reviews || [];
        if (!reviews.length) {
          return (
            <div className="fv-empty-state">
              <span className="fv-empty-state__icon" aria-hidden="true">
                <i className="fa-regular fa-star" />
              </span>
              <p>Be the first to review after your stay.</p>
            </div>
          );
        }
        const avgRating =
          reviews.reduce((sum, r) => sum + (Number(r.rating) || 0), 0) /
          reviews.length;
        return (
          <>
            <div className="fv-reviews-summary">
              <div className="fv-reviews-summary__score">
                <strong>{avgRating.toFixed(1)}</strong>
                <StarRating rating={Math.round(avgRating)} />
              </div>
              <span className="fv-reviews-summary__count">
                {reviews.length} review{reviews.length !== 1 ? "s" : ""}
              </span>
            </div>
            <ul className="fv-reviews">
              {reviews.map((review, index) => {
                const name = review.user_name || "Guest";
                const initial = name.charAt(0).toUpperCase();
                return (
                  <li key={review.id || index} className="fv-review-card">
                    <div className="fv-review-card__head">
                      <span
                        className="fv-review-card__avatar"
                        aria-hidden="true"
                      >
                        {initial}
                      </span>
                      <div className="fv-review-card__meta">
                        <strong>{name}</strong>
                        <time dateTime={review.created_at}>
                          {new Date(review.created_at).toLocaleDateString(
                            "en-IN",
                            {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            },
                          )}
                        </time>
                      </div>
                      <span className="fv-review-card__stars">
                        <StarRating rating={review.rating || 0} />
                      </span>
                    </div>
                    <p className="fv-review-card__body">{review.message}</p>
                  </li>
                );
              })}
            </ul>
          </>
        );
      }
      case "location":
        return (
          <div className="fv-map-card">
            <div className="fv-map-card__frame">
              <iframe
                title="Hotel location map"
                src={
                  hotel.lat && hotel.lng
                    ? `https://www.google.com/maps?q=${hotel.lat},${hotel.lng}&z=15&output=embed`
                    : `https://www.google.com/maps?q=${encodeURIComponent(
                        hotel.address || "India",
                      )}&z=15&output=embed`
                }
                loading="lazy"
              />
            </div>
            <div className="fv-map-card__footer">
              <i className="fa-solid fa-location-dot" aria-hidden="true" />
              <p>{hotel.address || "Address not available"}</p>
            </div>
          </div>
        );
      case "nearby":
        if (!nearbyHotels?.length) {
          return <p className="fv-empty">No nearby hotels found.</p>;
        }
        return (
          <div className="fv-nearby-rail" role="list">
            {nearbyHotels.map((h, index) => (
              <article
                key={h.id || h.slug || index}
                className="fv-nearby-rail__item"
                role="listitem"
              >
                <button
                  type="button"
                  className="fv-nearby-chip"
                  onClick={() => navigate(`/hotels/${h.slug}`)}
                >
                  <span className="fv-nearby-chip__img">
                    {h.image ? (
                      <LazyImage src={h.image} alt="" />
                    ) : (
                      <span
                        className="fv-nearby-chip__placeholder"
                        aria-hidden="true"
                      >
                        <i className="fa-solid fa-hotel" />
                      </span>
                    )}
                  </span>
                  <span className="fv-nearby-chip__body">
                    <strong className="fv-nearby-chip__name">{h.name}</strong>
                    <span className="fv-nearby-chip__foot">
                      <span className="fv-nearby-chip__price">
                        From ₹{h.price}
                      </span>
                    </span>
                  </span>
                </button>
              </article>
            ))}
          </div>
        );
      case "room-details":
        if (!selectedRoomId) {
          return (
            <p className="fv-empty">Select a room to view room details.</p>
          );
        }

        if (roomLoading) {
          return (
            <div className="fv-empty">
              <p>Loading room details...</p>
            </div>
          );
        }

        if (roomError) {
          return (
            <div className="fv-empty">
              <p>{roomError}</p>
            </div>
          );
        }

        if (!roomDetails) {
          return <p className="fv-empty">Room details not available.</p>;
        }

        const roomImages = [
          roomDetails.featured_image,
          ...(Array.isArray(roomDetails.images) ? roomDetails.images : []),
        ].filter(Boolean);

        return (
          <div className="fv-room-details">
            {/* ROOM TITLE */}
            <div className="fv-room-details__header">
              <h3>{roomDetails.name}</h3>

              {roomDetails.type?.name && (
                <span className="fv-section__badge">
                  {roomDetails.type.name}
                </span>
              )}
            </div>

            {/* ROOM PHOTOS */}
            {roomImages.length > 0 && (
              <div className="fv-gallery">
                <div className="fv-gallery__grid">
                  {roomImages.map((img, index) => (
                    <button
                      key={img + index}
                      type="button"
                      className={`fv-gallery__cell ${
                        index === 0 && roomImages.length > 2
                          ? "fv-gallery__cell--feature"
                          : ""
                      }`}
                      onClick={() => {
                        // Room images ko lightbox me dikhane ke liye
                        setPhotoIndex(index);
                        setIsOpen(true);
                      }}
                    >
                      <LazyImage src={img} alt={roomDetails.name || "Room"} />

                      <span className="fv-gallery__overlay" aria-hidden="true">
                        <i className="fa-solid fa-expand" />
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ROOM INFO */}
            <div className="fv-room-info">
              <div className="fv-room-info__item">
                <i className="fa-solid fa-bed" />
                <span>
                  <strong>Bedrooms</strong>
                  {roomDetails.bedrooms || 0}
                </span>
              </div>

              <div className="fv-room-info__item">
                <i className="fa-solid fa-user" />
                <span>
                  <strong>Adults</strong>
                  {roomDetails.max_adults || 0}
                </span>
              </div>

              <div className="fv-room-info__item">
                <i className="fa-solid fa-child" />
                <span>
                  <strong>Children</strong>
                  {roomDetails.max_children || 0}
                </span>
              </div>

              <div className="fv-room-info__item">
                <i className="fa-solid fa-indian-rupee-sign" />
                <span>
                  <strong>Price</strong>₹{roomDetails.base_price || 0} / night
                </span>
              </div>
            </div>

            {/* DESCRIPTION */}
            {roomDetails.description && (
              <div className="fv-room-description">
                <h4>About this room</h4>
                <p>{roomDetails.description}</p>
              </div>
            )}

            {/* AMENITIES */}
            {Array.isArray(roomDetails.amenities) &&
              roomDetails.amenities.length > 0 && (
                <div className="fv-room-amenities">
                  <h4>Room Amenities</h4>

                  <ul className="fv-amenity-grid">
                    {roomDetails.amenities.map((amenity, index) => {
                      const iconUrl = amenity?.icon
                        ? `https://admin.bookvenue.app/${String(
                            amenity.icon,
                          ).replace(/^\/+/, "")}`
                        : null;

                      return (
                        <li key={amenity?.id || `${amenity?.name}-${index}`}>
                          <div className="fv-amenity-card">
                            <span className="fv-amenity-card__icon">
                              {iconUrl ? (
                                <img
                                  src={iconUrl}
                                  alt={amenity?.name || "Amenity"}
                                  loading="lazy"
                                />
                              ) : (
                                <i
                                  className={hotelAmenityIcon(
                                    amenity?.name || "Amenity",
                                  )}
                                />
                              )}
                            </span>

                            <span className="fv-amenity-card__name">
                              {amenity?.name || "Amenity"}
                            </span>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
          </div>
        );
      case "about":
      default:
        return (
          <div
            className={`fv-about ${aboutExpanded ? "is-expanded" : ""} ${
              aboutNeedsToggle ? "has-toggle" : ""
            }`}
          >
            <div className="fv-about__clip">
              <div className="fv-prose">
                <p>{aboutDescription}</p>
                <ul className="ht-about-meta">
                  <li>
                    <i className="fa-solid fa-clock" aria-hidden="true" />
                    Check-in from {hotel.checkInTime}
                  </li>
                  <li>
                    <i className="fa-solid fa-clock" aria-hidden="true" />
                    Check-out by {hotel.checkOutTime}
                  </li>
                  <li>
                    <i className="fa-solid fa-star" aria-hidden="true" />
                    Rated {hotel.rating?.average ?? "Not rated"} / 5
                  </li>
                </ul>
              </div>
            </div>
            {aboutNeedsToggle ? (
              <button
                type="button"
                className="fv-about__toggle"
                aria-expanded={aboutExpanded}
                onClick={() => setAboutExpanded((open) => !open)}
              >
                {aboutExpanded ? "View less" : "Read more"}
                <i
                  className={`fa-solid ${
                    aboutExpanded ? "fa-chevron-up" : "fa-chevron-down"
                  }`}
                  aria-hidden="true"
                />
              </button>
            ) : null}
          </div>
        );
    }
  };
  const lightboxImages = roomDetails?.images?.length
    ? [roomDetails.featured_image, ...roomDetails.images].filter(Boolean)
    : images;

  return (
    <div className="fv-page ht-detail-page">
      <div className="fv-split">
        <div className="fv-split__main">
          <header className="fv-hero">
            <div className="fv-hero__inner">
              <nav className="fv-hero__crumb" aria-label="Breadcrumb">
                <Link to="/" className="fv-hero__crumb-link">
                  <i className="fa-solid fa-house" aria-hidden="true" />
                  Home
                </Link>
                <span className="fv-hero__crumb-sep" aria-hidden="true">
                  <i className="fa-solid fa-chevron-right" />
                </span>
                <Link to="/hotels" className="fv-hero__crumb-link">
                  Hotels
                </Link>
                <span className="fv-hero__crumb-sep" aria-hidden="true">
                  <i className="fa-solid fa-chevron-right" />
                </span>
                <span className="fv-hero__crumb-current" aria-current="page">
                  {hotel.name}
                </span>
              </nav>

              <div className="fv-hero__panel">
                <div className="fv-hero__row">
                  <div className="fv-hero__visual">
                    <div className="fv-hero__slider">
                      {heroImg ? (
                        <button
                          type="button"
                          className="fv-hero__slide"
                          onClick={() => {
                            setPhotoIndex(bannerSlide);
                            setIsOpen(true);
                          }}
                          aria-label="Open hotel photos"
                        >
                          <LazyImage
                            key={heroImg}
                            src={heroImg}
                            alt={hotel.name || "Hotel"}
                            loading="eager"
                          />
                        </button>
                      ) : (
                        <div className="fv-hero__slide fv-hero__slide--empty">
                          <i className="fa-solid fa-hotel" aria-hidden="true" />
                        </div>
                      )}

                      {images.length > 1 && (
                        <>
                          <button
                            type="button"
                            className="fv-hero__nav fv-hero__nav--prev"
                            aria-label="Previous photo"
                            onClick={() =>
                              setBannerSlide(
                                (bannerSlide - 1 + images.length) %
                                  images.length,
                              )
                            }
                          >
                            <i
                              className="fa-solid fa-chevron-left"
                              aria-hidden="true"
                            />
                          </button>
                          <button
                            type="button"
                            className="fv-hero__nav fv-hero__nav--next"
                            aria-label="Next photo"
                            onClick={() =>
                              setBannerSlide((bannerSlide + 1) % images.length)
                            }
                          >
                            <i
                              className="fa-solid fa-chevron-right"
                              aria-hidden="true"
                            />
                          </button>
                          <span className="fv-hero__count" aria-live="polite">
                            {bannerSlide + 1} / {images.length}
                          </span>
                        </>
                      )}

                      {images.length > 0 && (
                        <button
                          type="button"
                          className="fv-hero__gallery"
                          onClick={() => {
                            setPhotoIndex(bannerSlide);
                            setIsOpen(true);
                          }}
                        >
                          <i
                            className="fa-solid fa-images"
                            aria-hidden="true"
                          />
                          Gallery
                        </button>
                      )}
                    </div>

                    {images.length > 1 && (
                      <div
                        className="fv-hero__thumbs"
                        role="tablist"
                        aria-label="Photos"
                      >
                        {images.slice(0, 5).map((img, idx) => (
                          <button
                            key={img + idx}
                            type="button"
                            role="tab"
                            aria-selected={bannerSlide === idx}
                            className={bannerSlide === idx ? "is-active" : ""}
                            aria-label={`Photo ${idx + 1}`}
                            onClick={() => setBannerSlide(idx)}
                          >
                            <LazyImage src={img} alt="" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="fv-hero__content">
                    <div className="fv-hero__top">
                      <span className="fv-hero__eyebrow ht-hero-eyebrow">
                        <i className="fa-solid fa-hotel" aria-hidden="true" />
                        {hotel.category || "Hotel"}
                      </span>
                      <button
                        type="button"
                        className="fv-btn fv-btn--ghost fv-hero__btn fv-hero__btn--share"
                        onClick={handleShare}
                      >
                        <i
                          className="fa-solid fa-share-nodes"
                          aria-hidden="true"
                        />
                        Share
                      </button>
                    </div>

                    <h1 className="fv-hero__title">{hotel.name || "Hotel"}</h1>

                    <p className="fv-hero__loc">
                      <i
                        className="fa-solid fa-location-dot"
                        aria-hidden="true"
                      />
                      {hotel.address || "Address not available"}
                    </p>

                    <div className="fv-hero__pills">
                      <span className="fv-hero__pill">
                        <i
                          className="fa-solid fa-circle-check"
                          aria-hidden="true"
                        />
                        Verified hotel
                      </span>
                      <span className="fv-hero__pill">
                        <i className="fa-solid fa-bolt" aria-hidden="true" />
                        Instant booking
                      </span>
                      {hotel.rating?.average !== null &&
                        hotel.rating?.average !== undefined && (
                          <span className="fv-hero__pill">
                            <i
                              className="fa-solid fa-star"
                              aria-hidden="true"
                            />
                            {hotel.rating.average} rating
                          </span>
                        )}
                    </div>

                    <div className="fv-hero__stats">
                      <div className="fv-hero__stat">
                        <i
                          className="fa-solid fa-indian-rupee-sign"
                          aria-hidden="true"
                        />
                        <div>
                          <span>Starting at</span>
                          <strong>
                            ₹{roomDetails?.base_price ?? hotel.price ?? 0}/night
                          </strong>
                        </div>
                      </div>
                      <div className="fv-hero__stat">
                        <i
                          className="fa-solid fa-calendar-check"
                          aria-hidden="true"
                        />
                        <div>
                          <span>Check-in</span>
                          <strong>{hotel.checkInTime || "2:00 PM"}</strong>
                        </div>
                      </div>
                    </div>

                    <div className="fv-hero__footer">
                      <button
                        type="button"
                        className="fv-btn fv-btn--primary fv-hero__btn fv-hero__btn--book"
                        onClick={scrollToBook}
                      >
                        <i
                          className="fa-solid fa-calendar-check"
                          aria-hidden="true"
                        />
                        Reserve a room
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </header>

          <div className="fv-stage">
            <div className="fv-stage__layout">
              <nav
                className="fv-side-nav"
                ref={sideNavRef}
                role="tablist"
                aria-label="Hotel sections"
              >
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === item.id}
                    className={`fv-side-nav__btn ${
                      activeTab === item.id ? "is-active" : ""
                    }`}
                    onClick={(e) => selectTab(item.id, e)}
                    title={item.label}
                  >
                    <i className={item.icon} aria-hidden="true" />
                    <span>{item.label}</span>
                  </button>
                ))}
              </nav>

              <main className="fv-content" aria-label="Hotel details">
                {navItems.map((item) => (
                  <section
                    key={item.id}
                    id={`fv-section-${item.id}`}
                    data-section-id={item.id}
                    className={`fv-section fv-section--${item.id}`}
                    aria-labelledby={`fv-section-heading-${item.id}`}
                  >
                    <header className="fv-section__head">
                      <span className="fv-section__icon" aria-hidden="true">
                        <i className={item.icon} />
                      </span>
                      <div className="fv-section__titles">
                        <h2 id={`fv-section-heading-${item.id}`}>
                          {item.label}
                        </h2>
                        <p className="fv-section__desc">{item.hint}</p>
                      </div>
                      {item.id === "gallery" &&
                        (() => {
                          const activeImages = selectedRoomId
                            ? roomDetails
                              ? [
                                  roomDetails.featured_image,
                                  ...(roomDetails.images || []),
                                ].filter(Boolean)
                              : []
                            : images;

                          return activeImages.length > 0 ? (
                            <span className="fv-section__badge">
                              {activeImages.length} photos
                            </span>
                          ) : null;
                        })()}

                      {item.id === "amenities" &&
                      (roomDetails?.amenities?.length ||
                        hotel.amenities?.length) > 0 ? (
                        <span className="fv-section__badge">
                          {roomDetails?.amenities?.length ??
                            hotel.amenities.length}
                        </span>
                      ) : null}
                      {item.id === "nearby" && nearbyHotels?.length > 0 ? (
                        <span className="fv-section__badge">
                          {nearbyHotels.length} hotels
                        </span>
                      ) : null}
                    </header>
                    <div className="fv-section__body">
                      {renderSectionContent(item.id)}
                    </div>
                  </section>
                ))}
              </main>
            </div>
          </div>
        </div>

        <aside className="fv-split__book" aria-label="Reserve a room">
          <div className="fv-dock" id="book">
            <div
              className="fv-dock__panel fv-dock__panel--compact"
              id="fv-book-panel"
            >
              <header
                className="fv-dock__head"
                style={{
                  "--fv-booking-progress": `${((bookingStep - 1) / 3) * 100}%`,
                }}
              >
                <h2 className="fv-dock__venue">{hotel.name || "Hotel"}</h2>
                <ol className="fv-dock__stepper" aria-label="Booking progress">
                  {HOTEL_BOOKING_STEPS.map(({ step, label, icon }) => (
                    <li
                      key={step}
                      className={`fv-dock__stepper-item ${
                        bookingStep === step
                          ? "is-current"
                          : bookingStep > step
                            ? "is-done"
                            : ""
                      }`}
                      title={label}
                    >
                      <span
                        className="fv-dock__stepper-icon"
                        aria-hidden="true"
                      >
                        {bookingStep > step ? (
                          <i className="fa-solid fa-check" />
                        ) : (
                          <i className={icon} />
                        )}
                      </span>
                      <span className="fv-dock__stepper-label">{label}</span>
                    </li>
                  ))}
                </ol>
              </header>

              <div className="fv-dock__scroll">
                <p className="fv-dock__step-caption">
                  {bookingStepHints[bookingStep]}
                </p>
                <div className="fv-dock__form" key={bookingStep}>
                  {renderWizardStep()}
                </div>
              </div>

              <footer className="fv-dock__bar">
                <div className="fv-dock__summary" aria-label="Your stay">
                  <div className={`fv-dock__pill ${checkIn ? "is-set" : ""}`}>
                    <i
                      className="fa-solid fa-calendar-check"
                      aria-hidden="true"
                    />
                    <span className="fv-dock__pill-text">
                      <span className="fv-dock__pill-name">Check-in</span>
                      <span className="fv-dock__pill-value">
                        {formatChipDate(checkIn)}
                      </span>
                    </span>
                  </div>
                  <div className={`fv-dock__pill ${checkOut ? "is-set" : ""}`}>
                    <i
                      className="fa-solid fa-calendar-xmark"
                      aria-hidden="true"
                    />
                    <span className="fv-dock__pill-text">
                      <span className="fv-dock__pill-name">Check-out</span>
                      <span className="fv-dock__pill-value">
                        {formatChipDate(checkOut)}
                      </span>
                    </span>
                  </div>
                  <div
                    className={`fv-dock__pill ${selectedRoomId ? "is-set" : ""}`}
                  >
                    <i className="fa-solid fa-bed" aria-hidden="true" />
                    <span className="fv-dock__pill-text">
                      <span className="fv-dock__pill-name">Room</span>
                      <span
                        className="fv-dock__pill-value"
                        title={selectedRoom?.name || undefined}
                      >
                        {selectedRoom?.name || "—"}
                      </span>
                    </span>
                  </div>
                  <div className={`fv-dock__pill ${guests ? "is-set" : ""}`}>
                    <i className="fa-solid fa-users" aria-hidden="true" />
                    <span className="fv-dock__pill-text">
                      <span className="fv-dock__pill-name">Guests</span>
                      <span className="fv-dock__pill-value">
                        {guests
                          ? `${guests} guest${guests === 1 ? "" : "s"}`
                          : "—"}
                      </span>
                    </span>
                  </div>
                </div>

                <div className="fv-dock__pay">
                  <div className="fv-dock__pay-meta">
                    <span className="fv-dock__pay-label">Total</span>
                    <strong className="fv-dock__pay-amount">
                      {totalPrice > 0 ? `₹${totalPrice}` : "—"}
                    </strong>
                  </div>
                  <div className="fv-dock__pay-actions">
                    {hasBookingDraft ? (
                      <button
                        type="button"
                        className="fv-dock__pay-clear"
                        onClick={onClearBooking}
                        aria-label="Clear booking"
                      >
                        <i
                          className="fa-solid fa-rotate-left"
                          aria-hidden="true"
                        />
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="fv-dock__pay-btn"
                      disabled={!canBook || paying}
                      onClick={handleReserve}
                    >
                      {paying ? "Processing…" : bookCtaLabel()}
                    </button>
                  </div>
                </div>
                {bookingError && (
                  <p
                    role="alert"
                    style={{ color: "#d33", fontSize: 13, margin: "6px 0 0" }}
                  >
                    {bookingError}
                  </p>
                )}
                {confirmedBooking && (
                  <p
                    role="status"
                    style={{
                      color: "#1a8a3a",
                      fontSize: 13,
                      margin: "6px 0 0",
                    }}
                  >
                    ✅ Booking #{confirmedBooking.booking_id} confirmed · ₹
                    {confirmedBooking.total_price}
                  </p>
                )}

                <p className="fv-dock__note">
                  <i className="fa-solid fa-lock" aria-hidden="true" />
                  Secure payment · Instant confirmation
                </p>
              </footer>
            </div>
          </div>
        </aside>
      </div>

      {isOpen && lightboxImages.length > 0 && (
        <Lightbox
          mainSrc={lightboxImages[photoIndex]}
          nextSrc={lightboxImages[(photoIndex + 1) % lightboxImages.length]}
          prevSrc={
            lightboxImages[
              (photoIndex + lightboxImages.length - 1) % lightboxImages.length
            ]
          }
          onCloseRequest={() => setIsOpen(false)}
          onMovePrevRequest={() =>
            setPhotoIndex(
              (photoIndex + lightboxImages.length - 1) % lightboxImages.length,
            )
          }
          onMoveNextRequest={() =>
            setPhotoIndex((photoIndex + 1) % lightboxImages.length)
          }
        />
      )}

      <div className="fv-mobile-bar">
        <div className="fv-mobile-bar__price">
          <span>From</span>
          <strong>₹{hotel.price || 0}</strong>
        </div>
        <button type="button" onClick={scrollToBook}>
          {canBook ? `Reserve · ₹${totalPrice}` : "Reserve a room"}
        </button>
      </div>
    </div>
  );
};

export default HotelDetailView;
